import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import { useLocation } from 'react-router-dom';

import useAuthenticated from '../../../../../../../../../../hooks/useAuthenticated';
import { MeForAlbumFoldersSelect } from '../../../../../../../../../../graphql/queries/account';
import { AlbumFolderForAlbumSelect } from '../../../../../../../../../../graphql/queries/album-folder';
import MultiSelect from '../../../../../../../../../../components/global/MultiSelect';
import BoxedCustomControl from '../../../../../../../../../../components/global/BoxedCustomControl';
import Label from '../../../../../../../../../../components/global/Label';
import Input from '../../../../../../../../../../components/global/Input';
import { getBasenameDefinitions } from '../../../../../../../../../../util/role-definitions';
import useBasename from '../../../../../../../../../../components/routing/useBasename';
import { getAlbumFolderAlbumFlatList } from '../../../../../../../../../../util/album-folder';

const propTypes = {
  contactLabel: PropTypes.string,
  contactLinkConfiguration: PropTypes.shape({
    albumFolderId: PropTypes.string,
    albumFolderName: PropTypes.string,
    albums: PropTypes.array,
    linkToGallery: PropTypes.bool,
    listName: PropTypes.string,
  }).isRequired,
  setContactLinkConfiguration: PropTypes.func.isRequired,
};

const ContactLinkSelector = ({
  contactLinkConfiguration,
  setContactLinkConfiguration,
}) => {
  const {
    albumFolderId,
    albums,
    linkToGallery = false,
    listName = '',
  } = contactLinkConfiguration;
  const basename = useBasename();
  const location = useLocation();
  const authenticated = useAuthenticated();
  const definitions = getBasenameDefinitions(basename);
  const { data: { me } = {} } = useQuery(MeForAlbumFoldersSelect, {
    variables: {
      album_type_subtype_web_app_basename_filters:
        definitions.allowedContentTypes,
    },
    skip: !authenticated,
  });
  const { data: { albumFolder } = {} } = useQuery(AlbumFolderForAlbumSelect, {
    variables: {
      id: albumFolderId,
    },
    skip: !albumFolderId,
  });

  useEffect(() => {
    if (!albumFolderId || albumFolder?.id !== albumFolderId) {
      setContactLinkConfiguration({
        ...contactLinkConfiguration,
        albums: [],
      });
    }
  }, [albumFolderId]);

  if (!authenticated || !location?.pathname?.includes('/address-book')) {
    return null;
  }

  const albumOptions = useMemo(() => {
    if (!albumFolder) {
      return [];
    }

    return getAlbumFolderAlbumFlatList(albumFolder, true);
  }, [albumFolder]);

  return (
    <div className="mt-1 mb-2">
      <h4 className="mb-1">Would you like to link this upload to a gallery?</h4>
      <p>
        {linkToGallery === false
          ? "Contacts linked to a gallery won't join the gallery or appear on the contact dashboard, but will be able to be filtered via the Relationship Filter on your address book."
          : 'Linking to list a gallery will allow you to easily send personalized Waldograms as each identity will have their matched photos accessible via the address book.'}
      </p>
      <div className="gallery-link-choice mb-2">
        <BoxedCustomControl
          id="link-gallery-yes"
          type="radio"
          label="Yes"
          checked={linkToGallery === true}
          onChange={() =>
            setContactLinkConfiguration({
              ...contactLinkConfiguration,
              linkToGallery: true,
            })
          }
          customControlProps={{ name: 'linkToGallery' }}
        />
        <BoxedCustomControl
          id="link-gallery-no"
          type="radio"
          label="No"
          checked={linkToGallery === false}
          onChange={() =>
            setContactLinkConfiguration({
              ...contactLinkConfiguration,
              linkToGallery: false,
              albumFolderId: null,
              albumFolderName: null,
              albums: [],
            })
          }
          customControlProps={{ name: 'linkToGallery' }}
        />
      </div>
      {linkToGallery && (
        <div
          className={`contact-link-selectors include-albums-select${
            albumFolderId ? '' : ' contact-link-selectors--empty'
          }`}
        >
          <MultiSelect
            id="album-folder-select"
            name="albumFolderSelect"
            label="Select gallery"
            data-testid="album-folder-select"
            placeholder="Select a gallery"
            value={
              albumFolder
                ? { value: albumFolder.id, label: albumFolder.name }
                : null
            }
            options={(me?.album_folders?.records ?? []).map(folder => ({
              label: folder.name,
              value: folder.id,
            }))}
            onChange={option => {
              setContactLinkConfiguration({
                ...contactLinkConfiguration,
                albumFolderId: option?.value,
                albumFolderName: option?.label,
              });
            }}
            isClearable
          />
          <MultiSelect
            id="albums-select"
            name="albumsSelect"
            label="Select album (optional)"
            data-testid="albums-select"
            placeholder={
              !albumFolderId || !albumFolder
                ? 'Select a gallery first...'
                : 'Select albums'
            }
            noOptionsMessage={() =>
              !albumFolderId || !albumFolder
                ? 'Select a gallery first...'
                : 'This gallery does not have any albums'
            }
            isDisabled={!albumFolderId || !albumFolder}
            value={albums.map(album => ({
              value: album.id,
              label: album.name,
            }))}
            options={albumOptions.map(album => ({
              label: album.name,
              value: album.id,
            }))}
            onChange={options => {
              setContactLinkConfiguration({
                ...contactLinkConfiguration,
                albums: options.map(opt => ({
                  id: opt.value,
                  name: opt.label,
                })),
              });
            }}
            isMulti
            isClearable
          />
        </div>
      )}
      {linkToGallery === false && (
        <div className="list-name-field mb-2">
          <Label id="list-name" label="Name your list" />
          <Input
            id="list-name"
            placeholder="Insert a name for your list..."
            value={listName}
            onChange={event =>
              setContactLinkConfiguration({
                ...contactLinkConfiguration,
                listName: event.target.value,
              })
            }
          />
        </div>
      )}
    </div>
  );
};

ContactLinkSelector.propTypes = propTypes;

export default ContactLinkSelector;
